/**
 * Copyright 2026 Salesforce, Inc.
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
import { useEffect, useMemo, useState } from 'react';
import { Calendar, ChevronDown } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useBasket } from '@/providers/basket';
import { useCurrency } from '@/providers/currency';
import { formatCurrency } from '@/lib/currency';
import { RadioGroup } from '@/components/forms/radio-group';
import { Typography } from '@/components/typography';
import { Button } from '@/components/buttons';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { parseIsoDateString } from '@/components/date-picker/date-picker.utils';
import { useCheckoutActions } from '@/hooks/use-checkout-actions';
import { useAutoPopulateDeliverySchedule } from '@/hooks/use-auto-populate-delivery-schedule';
import { getDeliveryShipment } from '@/extensions/bopis/lib/basket-utils';
import { formatDeliveryDate } from '@/lib/checkout/delivery-group-display';
import { getBasketBoolean, getShipmentString, normaliseDeliveryWindows } from '@/lib/checkout/ohds-basket-utils';
import { HdMethodCardDatePickerPopoverContent } from './hd-method-card-date-picker';
import RadioDeliveryMethodTile from './radio-delivery-methods-tile';

/**
 * Minimal structural shape the component actually consumes from
 * `useCheckoutActions`. Defining this locally (rather than `Pick<...>`-ing
 * the full hook return) keeps the test seam loose — tests can supply the
 * absolute minimum without re-typing every other action.
 *
 * INT-12 FE-08: extended to include the delivery-schedule slice so the
 * per-tile "Change date" popover (added on top of PR #114's
 * {@link RadioDeliveryMethodTile} shell) can PATCH the basket without
 * having to call `useCheckoutActions` a second time.
 */
export interface DeliveryMethodToggleActions {
    submitHdShippingType: (value: 'HD' | 'THD') => void;
    hdShippingTypeFetcher: { state: 'idle' | 'loading' | 'submitting' };
    submitDeliverySchedule: (selectedDate: string | null, options?: { isUserSelected?: boolean }) => void;
    deliveryScheduleFetcher: { state: 'idle' | 'loading' | 'submitting'; data?: { success?: boolean } | null };
}

/**
 * CHECKOUT-10 — Delivery Method Toggle (HD vs Doorstep / THD).
 *
 * Renders one {@link RadioDeliveryMethodTile} per option (Figma node
 * 34613-66829), letting the shopper choose between:
 *   • HD  — White Glove Delivery (service included → green check)
 *   • THD — Doorstep Delivery (service not included → red prohibited icon)
 *
 * Behavior:
 *   • Reads the current selection and calculated fees from `basket.c_hdShippingType`
 *     / `basket.c_hdFee` / `basket.c_thdFee` (populated by the CART-02 enrichment).
 *   • The "Get it by {date}" row is driven by the delivery shipment's
 *     `c_requestedDeliveryDatetime` (hidden when no date is present).
 *   • If the basket is not threshold-eligible (`c_thresholdEligible !== true`),
 *     renders a single read-only White Glove tile (static dot, no radio).
 *   • On change, fires `submitHdShippingType(value)` (a fire-and-forget radio
 *     change — NOT a form submission).
 *   • While the dedicated `hdShippingTypeFetcher` is submitting, the radios are
 *     disabled and a subtle loading indicator is shown.
 *   • A `null` fee is displayed as the i18n "Calculating…" placeholder; numeric
 *     fees (including 0) are formatted with `formatCurrency`.
 *
 * INT-12 FE-08 — OHDS date picker integration (post-PR #114 restoration):
 *   • Calls {@link useAutoPopulateDeliverySchedule} so the basket's
 *     `c_requestedDeliveryDatetime` gets stamped with the first available
 *     OHDS window the moment one becomes known (SYSTEM-default contract).
 *     This replaces the equivalent effect that used to live inside the
 *     unmounted `<HdMethodCardDatePicker/>`. The hook honours the OHDS
 *     master kill switch (`c_ohdsEnabled !== true` ⇒ no auto-populate).
 *   • Each tile is wrapped in a controlled Radix {@link Popover} anchored
 *     to the tile via {@link PopoverAnchor}. The tile's `onChangeDate`
 *     callback opens the popover; the popover body is the extracted
 *     calendar from `hd-method-card-date-picker.tsx`.
 *   • Selecting a date PATCHes the basket with `isUserSelected=true` and
 *     closes the popover.
 *   • The popover ALWAYS opens when "Change date" is clicked — it is never
 *     gated on `c_ohdsEnabled` or the presence of windows. When no windows
 *     are available (ineligible ZIP, OHDS off, pre-enrichment race), the
 *     popover body renders a friendly empty-state message instead of the
 *     calendar. This avoids the silent-no-op UX bug shipped on the first
 *     FE-08 cut: a visible button must always produce visible feedback.
 */
export type DeliveryMethodToggleVariant = 'standalone' | 'inline';

export interface DeliveryMethodToggleProps {
    /** Optional class name for the outer container. */
    className?: string;
    /**
     * @deprecated Layout is now unified on {@link RadioDeliveryMethodTile} cards;
     * this prop is accepted for caller compatibility but no longer alters layout.
     */
    variant?: DeliveryMethodToggleVariant;
    /**
     * Test seam: lets unit tests inject a stub for `useCheckoutActions` rather
     * than mocking the entire module. The default uses the real hook.
     */
    useActions?: () => DeliveryMethodToggleActions;
    /** Invoked when the "Delivery details" footer link is clicked on any tile. */
    onDeliveryDetails?: (methodName: string) => void;
    /** Invoked when the "Free details" footer link is clicked on any tile. */
    onFreeDetails?: (methodName: string) => void;
    /**
     * When true, hide "Schedule Your Delivery" and the per-tile "Get it by"
     * row (used when the HD group has any backordered items — shoppers cannot
     * pick a delivery date for that group).
     */
    hideSchedule?: boolean;
}

const HEADING_ID = 'delivery-method-toggle-heading';

/** Build the first-of-month Date the popover should display when it opens. */
function makeSeedMonth(seedIso: string | null | undefined): Date {
    const parsed = parseIsoDateString(seedIso ?? undefined);
    return parsed ? new Date(parsed.getFullYear(), parsed.getMonth(), 1, 12) : new Date();
}

export default function DeliveryMethodToggle({
    className,
    useActions = useCheckoutActions,
    onDeliveryDetails,
    onFreeDetails,
    hideSchedule = false,
}: DeliveryMethodToggleProps) {
    const { t, i18n } = useTranslation('checkout');
    const basket = useBasket();
    const currency = useCurrency();
    const { submitHdShippingType, hdShippingTypeFetcher, submitDeliverySchedule, deliveryScheduleFetcher } =
        useActions();

    // FE-08 — keep the basket's `c_requestedDeliveryDatetime` populated even
    // when the shopper never opens the popover. Without this, the tile's
    // "Get it by …" row (and therefore the "Change date" trigger) would
    // stay hidden until the backend `shipping_address.afterPUT` SYSTEM
    // fallback writes the date — i.e. only after the shopper re-PUTs the
    // shipping address. Forward the caller's `useActions` so the
    // auto-populate hook shares the same fetcher instance as the popover
    // wiring below (avoids two parallel `useCheckoutActions()` calls in
    // tests that stub it). Skip when scheduling is hidden (e.g. backorder).
    useAutoPopulateDeliverySchedule({ useActions, enabled: !hideSchedule });

    const thresholdEligible = basket?.c_thresholdEligible === true;
    const currentValue: 'HD' | 'THD' = basket?.c_hdShippingType === 'THD' ? 'THD' : 'HD';
    const isSubmitting = hdShippingTypeFetcher.state === 'submitting';

    const hdFeeRaw = basket?.c_hdFee;
    const thdFeeRaw = basket?.c_thdFee;
    const hdFee = typeof hdFeeRaw === 'number' ? hdFeeRaw : null;
    const thdFee = typeof thdFeeRaw === 'number' ? thdFeeRaw : null;

    const formatFee = (fee: number | null) =>
        fee === null ? t('shipping.method.feeCalculating') : formatCurrency(fee, i18n.language, currency);

    const headingLabel = t('shipping.method.whiteGlove');

    // OHDS state — read from the delivery shipment (BOPIS-aware via
    // `getDeliveryShipment`). Eligibility (`c_ohdsAvailable`, which folds
    // master switch + ZIP eligibility into a single fresh-per-response
    // signal) is honoured both inside `useAutoPopulateDeliverySchedule`
    // above and on the `canSchedule` gate below.
    const deliveryShipment = getDeliveryShipment(basket);
    const persistedDate = getShipmentString(deliveryShipment, 'c_requestedDeliveryDatetime');
    const rawWindows = (deliveryShipment as unknown as Record<string, unknown> | undefined)?.c_deliveryWindows;
    const availableDates = useMemo(() => normaliseDeliveryWindows(rawWindows).sort(), [rawWindows]);
    const hasWindows = availableDates.length > 0;
    const isScheduleSubmitting = deliveryScheduleFetcher.state === 'submitting';

    // `c_ohdsAvailable` is the basket-level "is OHDS scheduling actually
    // possible for the current shipping ZIP?" signal. Critical: this is
    // recomputed FRESH on every basket response by
    // `BasketEnrichmentHelper.enrichBasketResponseHDShipping` via
    // `ZipMapUtil.ohdsZipMapCheck(basket)` — a direct
    // `ZipCodeMapping[<countryCode>-<currentZip>]` lookup. Unlike the
    // per-shipment `c_deliveryWindows` array (which only refreshes when
    // the `shipping_address.afterPUT` recompute hook fires), this signal
    // tracks the basket's CURRENT shipping address regardless of whether
    // the recompute path is healthy on a given instance. Combined with
    // the master kill switch — `c_ohdsAvailable = c_ohdsEnabled &&
    // ohdsZipMapCheck` — it is the single canonical "should we let the
    // shopper schedule a date?" answer.
    const ohdsAvailable = getBasketBoolean(basket, 'c_ohdsAvailable');

    // Single canonical "schedule row" gate, anchored to ZIP eligibility.
    //
    //   • Eligible ZIP → render the row (Calendar icon + Change date
    //     button) regardless of whether `c_deliveryWindows` and
    //     `c_requestedDeliveryDatetime` have arrived yet. Clicking
    //     Change date opens the popover, whose body adapts (calendar
    //     when windows are present, empty-state when not). This is the
    //     FE-08 "popover always opens" behaviour anchored to a stable
    //     ZIP-level signal.
    //
    //   • Ineligible ZIP → hide the row entirely. No stale "Get it by
    //     <prior eligible date>", no Change date affordance.
    //
    // `c_ohdsAvailable` is the right gate — it is recomputed FRESH on
    // every basket response from the basket's CURRENT shipping ZIP, so
    // it tracks the live address even when the per-shipment write path
    // (`shipping_address.afterPUT` recompute) hasn't refreshed
    // `c_deliveryWindows` / `c_requestedDeliveryDatetime`.
    //
    // The "Get it by <date>" text is independent — it shows ONLY when a
    // committed `persistedDate` exists. So the row has two visual
    // states for an eligible ZIP:
    //   eligible + persistedDate set → "Get it by <date> | Change date"
    //   eligible + no persistedDate  → just "Change date"
    const canSchedule = ohdsAvailable && !hideSchedule;
    const deliveryDate = canSchedule && persistedDate ? formatDeliveryDate(persistedDate, i18n.language) : null;

    // Single shared popover for the whole Ashley Home Delivery section.
    const [isSchedulePopoverOpen, setIsSchedulePopoverOpen] = useState(false);
    const [displayMonth, setDisplayMonth] = useState(() => makeSeedMonth(persistedDate ?? availableDates[0]));

    // Re-seed `displayMonth` to track the active selection when the popover
    // is closed. Only runs while the popover is closed so navigating to a
    // different month inside an open popover isn't immediately overwritten.
    useEffect(() => {
        if (isSchedulePopoverOpen) return;
        setDisplayMonth(makeSeedMonth(persistedDate ?? availableDates[0]));
    }, [isSchedulePopoverOpen, persistedDate, availableDates]);

    const handleSelectDate = (iso: string) => {
        submitDeliverySchedule(iso, { isUserSelected: true });
        setIsSchedulePopoverOpen(false);
    };

    /**
     * Renders the popover body. Extracted so the read-only branch and the
     * eligible branch don't have to repeat the wiring per tile.
     *
     * The empty-state branch (`!hasWindows`) is now defence-in-depth only —
     * the tile's "Get it by … | Change date" row is hidden in that state
     * (`deliveryDate = hasWindows ? formatDeliveryDate(...) : null` above),
     * so the popover trigger doesn't render and the body is unreachable
     * via normal user flow. Kept as a guard in case a future code path
     * mounts the popover programmatically or with different gating.
     */
    const renderPopoverContent = () => (
        <PopoverContent
            align="start"
            sideOffset={8}
            className="w-[19rem] rounded-md border-border p-0"
            data-testid="delivery-method-toggle-popover">
            {hasWindows ? (
                <HdMethodCardDatePickerPopoverContent
                    availableDates={availableDates}
                    selectedDate={persistedDate}
                    displayMonth={displayMonth}
                    onDisplayMonthChange={setDisplayMonth}
                    onSelect={handleSelectDate}
                    isSubmitting={isScheduleSubmitting}
                    methodKey="hd"
                />
            ) : (
                <div className="p-4 text-sm text-muted-foreground" data-testid="delivery-method-toggle-popover-empty">
                    {t('deliverySchedule.noDatesAvailable')}
                </div>
            )}
        </PopoverContent>
    );

    const renderSchedulePicker = () => {
        if (!canSchedule) return null;

        return (
            <div className="mb-3 py-3" data-testid="delivery-method-toggle-schedule">
                <Typography variant="p" className="text-sm font-semibold text-foreground">
                    {t('shipping.scheduleYourDelivery')}
                </Typography>
                <Popover open={isSchedulePopoverOpen} onOpenChange={setIsSchedulePopoverOpen}>
                    <PopoverTrigger asChild>
                        <Button
                            type="button"
                            variant="ghost"
                            className="mt-2 h-auto w-[22rem] max-w-full justify-between rounded-sm border border-border bg-background px-3 py-2 text-sm font-normal text-foreground hover:bg-secondary"
                            data-testid="delivery-method-toggle-schedule-trigger">
                            <span className="inline-flex items-center gap-2">
                                <Calendar className="size-4 text-muted-foreground" aria-hidden="true" />
                                <span>{deliveryDate ?? t('shipping.pickDeliveryDate')}</span>
                            </span>
                            <ChevronDown className="size-4 text-muted-foreground" aria-hidden="true" />
                        </Button>
                    </PopoverTrigger>
                    {renderPopoverContent()}
                </Popover>
                <Typography variant="small" className="mt-2 text-xs text-muted-foreground">
                    {t('shipping.deliveryDateDisclaimer')}
                </Typography>
            </div>
        );
    };

    // FE-08 — `onChangeDate` is wired UNCONDITIONALLY. The earlier
    // `canOpenPopover` gate turned the button into a silent no-op whenever
    // the basket lacked windows or the master switch wasn't `true` in the
    // response, which is bad UX (user clicks → nothing visible happens) AND
    // is hard to debug. The popover now always opens; the body inside it
    // adapts to the available data (calendar when windows are present,
    // friendly empty-state message otherwise).
    //
    // The auto-populate hook above still respects the master kill switch
    // independently, so a basket that returns `c_ohdsEnabled=false` will
    // never get an unsolicited `c_requestedDeliveryDatetime` PATCH. We just
    // don't punish the shopper at the UI layer for ambiguous server data.

    if (!thresholdEligible) {
        // Read-only branch — shopper not eligible for the THD toggle. Renders a
        // single White Glove tile with a non-interactive (filled) radio dot.
        return (
            <section
                className={className}
                role="group"
                aria-labelledby={HEADING_ID}
                data-testid="delivery-method-toggle-readonly">
                <h3 id={HEADING_ID} className="sr-only">
                    {headingLabel}
                </h3>
                <Typography variant="small" className="mb-2 text-xs text-muted-foreground">
                    {t('shipping.method.callToSchedule')}
                </Typography>
                {renderSchedulePicker()}
                <RadioDeliveryMethodTile
                    control="dot"
                    status="included"
                    selected
                    title={t('shipping.method.whiteGlove')}
                    price={formatFee(hdFee)}
                    description={t('shipping.method.whiteGloveDescription')}
                    deliveryDate={deliveryDate}
                    showChangeDate={false}
                    hideScheduleRow={hideSchedule}
                    onDeliveryDetails={() => onDeliveryDetails?.(t('shipping.method.whiteGlove'))}
                    onFreeDetails={() => onFreeDetails?.(t('shipping.method.whiteGlove'))}
                    data-testid="delivery-method-tile-hd"
                />
            </section>
        );
    }

    return (
        <section className={className} role="group" aria-labelledby={HEADING_ID} data-testid="delivery-method-toggle">
            <h3 id={HEADING_ID} className="sr-only">
                {headingLabel}
            </h3>
            {renderSchedulePicker()}
            <RadioGroup
                value={currentValue}
                onValueChange={(value) => {
                    if (value === 'HD' || value === 'THD') {
                        submitHdShippingType(value);
                    }
                }}
                disabled={isSubmitting}
                aria-labelledby={HEADING_ID}
                aria-busy={isSubmitting}
                className="flex flex-col gap-3">
                {/* HD — White Glove (service included → green check) */}
                <RadioDeliveryMethodTile
                    value="HD"
                    control="radio"
                    status="included"
                    selected={currentValue === 'HD'}
                    disabled={isSubmitting}
                    title={t('shipping.method.whiteGlove')}
                    price={formatFee(hdFee)}
                    description={t('shipping.method.whiteGloveDescription')}
                    deliveryDate={deliveryDate}
                    showChangeDate={false}
                    hideScheduleRow={hideSchedule}
                    onDeliveryDetails={() => onDeliveryDetails?.(t('shipping.method.whiteGlove'))}
                    onFreeDetails={() => onFreeDetails?.(t('shipping.method.whiteGlove'))}
                    id="delivery-method-toggle-hd"
                    radioTestId="delivery-method-toggle-radio-hd"
                    data-testid="delivery-method-tile-hd"
                />
                {/* THD — Doorstep (service not included → red prohibited icon) */}
                <RadioDeliveryMethodTile
                    value="THD"
                    control="radio"
                    status="excluded"
                    selected={currentValue === 'THD'}
                    disabled={isSubmitting}
                    title={t('shipping.method.doorstep')}
                    price={formatFee(thdFee)}
                    description={t('shipping.method.doorstepDescription')}
                    deliveryDate={deliveryDate}
                    showChangeDate={false}
                    hideScheduleRow={hideSchedule}
                    onDeliveryDetails={() => onDeliveryDetails?.(t('shipping.method.doorstep'))}
                    onFreeDetails={() => onFreeDetails?.(t('shipping.method.doorstep'))}
                    id="delivery-method-toggle-thd"
                    radioTestId="delivery-method-toggle-radio-thd"
                    data-testid="delivery-method-tile-thd"
                />
            </RadioGroup>
            {isSubmitting && (
                <Typography variant="small" className="mt-2 text-muted-foreground" aria-live="polite">
                    {t('shipping.method.feeCalculating')}
                </Typography>
            )}
        </section>
    );
}
